import {
  ProductCardVariantsQuery,
  useProductCardVariantQuery,
  useProductCardVariantsQuery,
} from '@graphql/queries/__generated__/product-card.generated'
import BoxIcon from '@icons/BoxIcon'
import PdfFileIcon from '@icons/PdfFileIcon'
import Skeleton from '@mui/material/Skeleton'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickAddLayoutEnum } from '../../../../generated/gqlTypes'
import AddedToCartNotice from './AddedToCartNotice'
import { ExtraRow, ExtraRowContext, Price, PriceRow, SUBMIT_HEIGHT, SubmitButton, TypeButton } from './common'
import KitSelection from './KitSelection'
import VariantSelect, { getOptionsName, VariantOption } from './VariantSelect'
import VariantSelection, { isDigital } from './VariantSelection'
import WholesaleSelection from './WholesaleSelection'

type VariantProperty = ProductCardVariantsQuery['specific']['product']['variantsProperties'][number]

// One option per variant; a variant with several properties gets them joined into one label.
const groupVariantOptions = (props: VariantProperty[]): VariantOption[] => {
  const byVariation = new Map<number, VariantOption>()
  props.forEach(prop => {
    const option = byVariation.get(prop.variation)
    if (option) {
      option.label = `${option.label} / ${prop.valueName}`
      return
    }
    byVariation.set(prop.variation, {
      pk: prop.variation,
      label: prop.valueName,
      attributeName: prop.attributeName,
      isSupply: prop.isSupply,
      shippingRequired: prop.shippingRequired,
      stockStatus: prop.stockStatus,
    })
  })
  return [...byVariation.values()]
}

// The common "PDF pattern or KIT" product gets two toggle buttons instead of a select.
const getPatternTypePair = (options: VariantOption[]) => {
  if (options.length !== 2) return null
  const digital = options.find(isDigital)
  const kit = options.find(option => option.isSupply)
  return digital && kit ? { digital, kit } : null
}

export type PatternType = 'kit' | 'digital'

const skeletonSx = { borderRadius: '8px' }

// Shaped like the form that will load (the layout comes with the product list), so the panel doesn't change its
// height when the data arrives.
const PanelSkeleton: React.FC<{ layout: QuickAddLayoutEnum }> = ({ layout }) => (
  <div className='product-card-panel' aria-busy='true'>
    {layout === QuickAddLayoutEnum.TypeButtons && (
      <div className='product-card-panel__types'>
        <Skeleton variant='rectangular' animation='wave' height={40} className='flex-1' sx={skeletonSx} />
        <Skeleton variant='rectangular' animation='wave' height={40} className='flex-1' sx={skeletonSx} />
      </div>
    )}
    {layout === QuickAddLayoutEnum.Select && (
      <Skeleton variant='rectangular' animation='wave' height={40} sx={skeletonSx} />
    )}
    <SelectionSkeleton />
    <Skeleton variant='rectangular' animation='wave' height={SUBMIT_HEIGHT} sx={skeletonSx} />
  </div>
)

// Uses the same fixed-height rows as the forms, so the panel keeps its height when the data arrives.
// The extra row stays collapsed while the next variant loads (no placeholder line to show).
const SelectionSkeleton: React.FC = () => (
  <>
    <div className='product-card-panel__price-row'>
      <Skeleton animation='wave' width={50} height={21} />
      <Skeleton animation='wave' width={80} height={24} />
    </div>
    <ExtraRow />
  </>
)

interface Props {
  specPk: number
  categoryName?: string
  // Wholesale mode: wholesale options and prices, adding to the wholesale cart (WholesaleSelection, which keeps the
  // panel open and never calls `onAdded`).
  wholesale?: boolean

  // After adding to the cart the panel shows AddedToCartNotice for a while, then asks to be closed.
  onClose(): void

  // The empty extra row collapsed or expanded, and whether the panel has finished opening (see ExtraRowContext).
  onExtraRowCollapsedChange?(collapsed: boolean): void
  settled?: boolean
  // The form the product will show (from the product list), for the loading skeleton. Unknown for a similar
  // product's slide, which falls back to the KIT/PDF shape.
  expectedLayout?: QuickAddLayoutEnum | null
  // KIT or PDF picked on another slide of the card: preselected here too when this product has the same pair.
  patternType?: PatternType | null

  onPatternTypeChange?(type: PatternType): void
}

const ProductCardPanel: React.FC<Props> = ({
  specPk,
  categoryName,
  wholesale = false,
  onClose,
  onExtraRowCollapsedChange,
  settled = true,
  expectedLayout,
  patternType,
  onPatternTypeChange,
}) => {
  const { t } = useTranslation()
  const [selectedPk, setSelectedPk] = useState<number | null>(null)
  const [added, setAdded] = useState(false)
  const lastExtraRowCollapsed = useRef(false)
  const firstRender = useRef(true)
  useEffect(() => {
    firstRender.current = false
  }, [])

  const variantsQuery = useProductCardVariantsQuery({ variables: { id: String(specPk), wholesale } })
  const specific = variantsQuery.data?.specific
  const product = specific?.product
  const options = useMemo(() => groupVariantOptions(product?.variantsProperties || []), [product])
  const pair = getPatternTypePair(options)
  // A product without variant properties has a single variant, found by the product pk.
  const isSingle = !!product && !options.length
  // The only option is picked right away and shown in a locked select.
  const onlyOptionPk = options.length === 1 ? options[0].pk : null
  const preselectedPk = pair && patternType ? pair[patternType].pk : null
  const currentPk = selectedPk ?? preselectedPk ?? onlyOptionPk
  const selectPatternType = (type: PatternType) => {
    setSelectedPk(pair[type].pk)
    onPatternTypeChange?.(type)
  }
  const variantQuery = useProductCardVariantQuery({
    variables: isSingle ? { productPk: product.pk, wholesale } : { pk: currentPk, wholesale },
    skip: !product || (!isSingle && !currentPk),
  })
  const variant = variantQuery.data?.productVariant
  const priceRange = wholesale ? product?.wholesalePriceRange : product?.priceRange
  const layout = !product
    ? expectedLayout || QuickAddLayoutEnum.TypeButtons
    : pair
    ? QuickAddLayoutEnum.TypeButtons
    : isSingle
    ? QuickAddLayoutEnum.Single
    : QuickAddLayoutEnum.Select

  // Wraps the skeleton too, so its extra row already has the loaded form's state.
  const withExtraRowContext = (content: React.ReactNode) => (
    <ExtraRowContext.Provider
      value={{
        initial: firstRender.current,
        settled,
        lastCollapsed: lastExtraRowCollapsed,
        onCollapsedChange: onExtraRowCollapsedChange,
      }}
    >
      {content}
    </ExtraRowContext.Provider>
  )

  if (variantsQuery.error) {
    return (
      <div className='product-card-panel'>
        <p className='product-card-panel__info'>{t('productCard.loadError', "Couldn't load product options.")}</p>
        <SubmitButton onClick={() => variantsQuery.refetch()}>{t('button.tryAgain', 'Try again')}</SubmitButton>
      </div>
    )
  }
  if (!product) {
    return withExtraRowContext(<PanelSkeleton layout={layout} />)
  }

  const chooseLabel = () => {
    if (pair) return t('productCard.choosePatternType', 'Choose pattern type')
    const name = getOptionsName(options)
    return name
      ? t('productCard.chooseAttributeButton', 'Choose {{name}}', { name })
      : t('productCard.chooseOptionButton', 'Choose option')
  }

  const renderSelection = () => {
    if (!isSingle && !currentPk) {
      return (
        <>
          <PriceRow muted>{priceRange && <Price range={priceRange} />}</PriceRow>
          <ExtraRow />
          <SubmitButton disabled>{chooseLabel()}</SubmitButton>
        </>
      )
    }
    if (!variant) {
      return (
        <>
          <SelectionSkeleton />
          <SubmitButton disabled>{t('button.addToCart', 'Add to Cart')}</SubmitButton>
        </>
      )
    }
    if (wholesale) {
      return <WholesaleSelection key={variant.pk} variant={variant} isArchive={product.isArchive} />
    }
    const formProps = {
      variant,
      productName: specific.name,
      isArchive: product.isArchive,
      categoryName,
      onAdded: () => setAdded(true),
    }
    // Keyed by variant: a fresh form (quantity 1) for every variant.
    return variant.isSupply ? (
      <KitSelection key={variant.pk} {...formProps} />
    ) : (
      <VariantSelection key={variant.pk} {...formProps} />
    )
  }

  return withExtraRowContext(
    <div className={`product-card-panel ${added ? 'product-card-panel--added' : ''}`}>
      {pair && (
        <div
          className='product-card-panel__types'
          role='radiogroup'
          aria-label={t('productCard.patternType', 'Pattern type')}
        >
          <TypeButton
            selected={currentPk === pair.kit.pk}
            icon={<BoxIcon viewBox='0 -960 960 960' />}
            label={t('productCard.kit', 'KIT')}
            onClick={() => selectPatternType('kit')}
          />
          <TypeButton
            selected={currentPk === pair.digital.pk}
            icon={<PdfFileIcon viewBox='0 0 400 400' />}
            label={t('productCard.pdfPattern', 'PDF Pattern')}
            onClick={() => selectPatternType('digital')}
          />
        </div>
      )}
      {!pair && !isSingle && (
        <VariantSelect options={options} selectedPk={currentPk} readOnly={!!onlyOptionPk} onChange={setSelectedPk} />
      )}
      {renderSelection()}
      {added && <AddedToCartNotice onClose={onClose} />}
    </div>
  )
}

export default ProductCardPanel
